import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faTimes } from '@fortawesome/free-solid-svg-icons';

import fuelEnquiryService from '../services/fuelEnquiryService';
import './RecapInvoicesPanel.css';

/**
 * The Customer Confirmation for this deal, opened from the SALES recap.
 *
 * Marcel, 2026-09-18: the confirmation should be visible "in Enq + Sales
 * recap". The recap payload already carries ``source_fuel_enquiry_id``, and
 * the enquiry endpoint already narrows a customer trader to their own
 * enquiries, so no new server gate exists here -- someone else's enquiry 404s
 * at the enquiry's own door.
 *
 * Reuses ``RecapInvoicesPanel.css`` so the recap's document panels match.
 */
const RecapCustomerConfirmationPanel = ({ enquiryId, enquiryDisplayId }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  // A failure must say so and offer a retry. Silently rendering nothing would
  // read as "this deal has no confirmation".
  const [loadError, setLoadError] = useState(null);

  const fileName = `Customer Confirmation ${enquiryDisplayId || enquiryId}.pdf`;

  const open = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const blob = await fuelEnquiryService.customerConfirmationPdf(enquiryId);
      setPdfUrl((previous) => {
        if (previous) window.URL.revokeObjectURL(previous);
        return window.URL.createObjectURL(blob);
      });
    } catch (err) {
      setLoadError(
        err?.response?.data?.error
        || err?.response?.data?.detail
        || err?.message
        || 'Could not open the Customer Confirmation.'
      );
    } finally {
      setLoading(false);
    }
  }, [enquiryId]);

  // Revoke the object URL on unmount so closing the recap does not leak it.
  useEffect(() => () => { if (pdfUrl) window.URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  const close = () => {
    setPdfUrl((previous) => {
      if (previous) window.URL.revokeObjectURL(previous);
      return null;
    });
  };

  if (!enquiryId) return null;

  return (
    <div className="recap-invoices-panel">
      <div className="recap-invoices-panel__header">
        <h2>Customer Confirmation</h2>
        <span className="recap-invoices-panel__note">
          The confirmation sent to the customer for this deal.
        </span>
      </div>

      {loadError && (
        <div className="recap-invoices-panel__error" role="alert">
          <p>{loadError}</p>
          <button type="button" onClick={open}>Try again</button>
        </div>
      )}

      {!pdfUrl && (
        <table className="recap-invoices-panel__table">
          <tbody>
            <tr>
              <td>{fileName}</td>
              <td>
                <button type="button" onClick={open} disabled={loading}>
                  <FontAwesomeIcon icon={faFilePdf} />{' '}
                  {loading ? 'Opening…' : 'View PDF'}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {pdfUrl && (
        <div className="recap-invoices-panel__viewer">
          <div className="recap-invoices-panel__viewer-bar">
            <span>{fileName}</span>
            <button type="button" onClick={close} aria-label="Close preview">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <iframe title={fileName} src={pdfUrl} />
        </div>
      )}
    </div>
  );
};

export default RecapCustomerConfirmationPanel;
